/**
 * Product reviews on the product page (worker/src/product-reviews.ts → GET /api/shop/product-reviews/:handle).
 *
 * Average, count, and the published reviews newest first, a page at a time. A review left for the
 * 5% thank-you says so on its face. The owner's reply sits under the review it answers.
 *
 * ⚠️ Visible text only — no structured data is emitted for these (verify:shop-faq-reviews holds
 * that line for the whole shop until it is decided on purpose).
 */
import { useEffect, useState, type MouseEvent } from "react";
import { get } from "./shared";

export interface ProductReviewView {
  id: string;
  rating: number;
  title: string | null;
  body: string | null;
  name: string;
  verified: boolean;
  incentivised: boolean;
  photos: string[];
  date: string;
  reply: string | null;
  replyDate: string | null;
}
export interface Page { average: number | null; count: number; reviews: ProductReviewView[]; page: number; pages: number }

/** Thank-you percent, as the shop words it on an incentivised review. Matches REVIEW_REWARD_PERCENT. */
const REWARD_PERCENT = 5;

const fmt = (iso: string) => new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });

/** Read-only stars: the rating is in the label, the glyphs are decoration. */
export function Stars({ value, size = "md" }: { value: number; size?: "sm" | "md" }) {
  const full = Math.round(value);
  return (
    <span className={`rv-stars rv-${size}`} role="img" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => <span key={n} aria-hidden="true" className={n <= full ? "on" : undefined}>★</span>)}
    </span>
  );
}

/** "Rated 4.5 out of 5, 12 reviews" — the one accessible name every rating display carries. */
export const ratingLabel = (average: number, count: number) => `Rated ${average.toFixed(1)} out of 5, ${count} review${count === 1 ? "" : "s"}`;

/**
 * Average stars, each filled 0, ½ or 1 (the average rounded to the nearest half), so 4.5 shows
 * exactly 4½ stars. Five fixed-width star boxes, spaced by gap — no letter-spacing to skew the clip.
 * The glyphs are decoration (aria-hidden). Standalone it is an image with the label; inside a link
 * (`inLink`) the label is visually-hidden text instead, so the link's name reads naturally.
 */
export function RatingStars({ average, count, size = "md", inLink = false }: { average: number; count: number; size?: "sm" | "md"; inLink?: boolean }) {
  const half = Math.round(average * 2) / 2;
  const label = ratingLabel(average, count);
  return (
    <>
      <span className={`rs rs-${size}`} {...(inLink ? {} : { role: "img", "aria-label": label })} aria-hidden={inLink ? "true" : undefined}>
        {[0, 1, 2, 3, 4].map((i) => {
          const fill = Math.max(0, Math.min(1, half - i));
          return (
            <span key={i} className="rs-star" aria-hidden="true">
              ★{fill > 0 && <span className="rs-fg" style={{ width: `${fill * 100}%` }}>★</span>}
            </span>
          );
        })}
      </span>
      {inLink && <span className="sr-only">{label}</span>}
    </>
  );
}

/** Under the product title: stars, the average and the count, jumping to the reviews below. Nothing at 0 reviews. */
export function RatingLink({ summary }: { summary: Summary | null }) {
  if (!summary || summary.count === 0 || summary.average === null) return null;
  const jump = (e: MouseEvent<HTMLAnchorElement>) => {
    /** Always ours: a raw "#reviews" would be read by the shop's router as a different page. */
    e.preventDefault();
    const el = document.getElementById("reviews");
    if (!el) return;
    const calm = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "start" });
    el.focus({ preventScroll: true });
  };
  return (
    <a className="rs-link" href="#reviews" onClick={jump}>
      <RatingStars average={summary.average} count={summary.count} inLink />
      <span aria-hidden="true"><b>{summary.average.toFixed(1)}</b> ({summary.count} review{summary.count === 1 ? "" : "s"})</span>
    </a>
  );
}

/**
 * A 1–5 star picker that is a real radio group, so a keyboard and a screen reader can use it.
 * ⚠️ `name` must be unique per picker on the page (the product id): two groups sharing a name are
 * ONE radio group to the browser, and picking stars on one item would clear the other.
 */
export function StarInput({ value, onChange, label, name }: { value: number; onChange: (n: number) => void; label: string; name: string }) {
  return (
    <div className="rv-input" role="radiogroup" aria-label={label}>
      {[1, 2, 3, 4, 5].map((n) => (
        <label key={n} className={n <= value ? "on" : undefined}>
          <input type="radio" name={name} value={n} checked={value === n} onChange={() => onChange(n)} />
          <span aria-hidden="true">★</span>
          <span className="sr-only">{n} star{n === 1 ? "" : "s"}</span>
        </label>
      ))}
    </div>
  );
}

export interface Summary { average: number | null; count: number }
export type ReviewList = { status: "loading" } | { status: "error" } | { status: "ok"; reviews: ProductReviewView[]; page: number; pages: number };
export interface ProductReviewsState { summary: Summary | null; list: ReviewList; setPage: (n: number) => void; retry: () => void }

/**
 * The product's reviews, fetched once per handle/page. The page owns this so the stars under the
 * title and the section share one request.
 *
 * The SUMMARY (average, count) and the paged LIST are kept apart: paging, or a page that fails to
 * load, never hides the title stars or unmounts the section. Everything is tagged with its handle
 * and read back only for that handle, so product B can never show product A's stars or reviews.
 */
export function useProductReviews(handle: string): ProductReviewsState {
  const [pg, setPg] = useState({ handle, page: 1 });
  const [sum, setSum] = useState<{ handle: string; value: Summary } | null>(null);
  const [res, setRes] = useState<{ key: string; list: ReviewList } | null>(null);
  const [tries, setTries] = useState(0);
  const page = pg.handle === handle ? pg.page : 1;
  const key = `${page}|${handle}`;

  useEffect(() => {
    const ac = new AbortController();
    get<{ average: number | null; count: number; reviews: ProductReviewView[]; page: number; pages: number }>(`/shop/product-reviews/${encodeURIComponent(handle)}?page=${page}`, ac.signal)
      .then((d) => {
        setSum({ handle, value: { average: d.average, count: d.count } });
        setRes({ key, list: { status: "ok", reviews: d.reviews, page: d.page, pages: d.pages } });
      })
      .catch(() => { if (!ac.signal.aborted) setRes({ key, list: { status: "error" } }); });
    return () => ac.abort();
  }, [handle, page, key, tries]);

  return {
    summary: sum && sum.handle === handle ? sum.value : null,
    list: res && res.key === key ? res.list : { status: "loading" },
    setPage: (n) => setPg({ handle, page: n }),
    retry: () => { setRes(null); setTries((t) => t + 1); },
  };
}

export function ProductReviewsSection({ rv }: { rv: ProductReviewsState }) {
  const { summary, list, setPage } = rv;
  /** No reviews yet → no section. An empty box reads as "nobody bought this". */
  if (!summary || summary.count === 0) return null;
  return (
    <section className="rv-section" aria-label="Customer reviews" id="reviews" tabIndex={-1}>
      <h2>Customer reviews</h2>
      <div className="rv-summary">
        {summary.average !== null && <RatingStars average={summary.average} count={summary.count} />}
        <span><b>{summary.average?.toFixed(1)}</b> out of 5 · {summary.count} review{summary.count === 1 ? "" : "s"}</span>
      </div>
      {list.status === "loading" && <p className="rv-mute" role="status">Loading reviews…</p>}
      {list.status === "error" && (
        <p className="rv-mute" role="alert">Reviews could not be loaded. <button type="button" className="btn btn-quiet" onClick={rv.retry}>Try again</button></p>
      )}
      {list.status === "ok" && (
        <>
          <ol className="rv-list">
            {list.reviews.map((r) => (
              <li key={r.id} className="rv-review">
                <div className="rv-top"><Stars value={r.rating} size="sm" />{r.title && <b>{r.title}</b>}</div>
                <div className="rv-by">{r.name}{r.verified ? " · Verified buyer" : ""} · {fmt(r.date)}</div>
                {r.body && <p className="rv-body">{r.body}</p>}
                {r.photos.length > 0 && (
                  <div className="rv-photos">{r.photos.map((u) => <a key={u} href={u} target="_blank" rel="noopener noreferrer"><img src={u} alt={`Photo from ${r.name}`} width={88} height={88} loading="lazy" /></a>)}</div>
                )}
                {r.incentivised && <div className="rv-note">Received a {REWARD_PERCENT}% discount for reviewing</div>}
                {r.reply && <div className="rv-reply"><b>Reply from the shop</b>{r.replyDate ? ` · ${fmt(r.replyDate)}` : ""}<p>{r.reply}</p></div>}
              </li>
            ))}
          </ol>
          {list.pages > 1 && (
            <div className="rv-pager">
              <button type="button" className="btn btn-quiet" disabled={list.page <= 1} onClick={() => setPage(list.page - 1)}>Newer</button>
              <span>Page {list.page} of {list.pages}</span>
              <button type="button" className="btn btn-quiet" disabled={list.page >= list.pages} onClick={() => setPage(list.page + 1)}>Older</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
